import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { UserRole, Provider } from '../types';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  User,
  Users,
  FileText,
  Clock,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  ShieldCheck,
  Stethoscope,
  Activity,
  Layers,
  ExternalLink,
  RefreshCw,
  Search,
  Filter,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';
import { motion } from 'motion/react';
import {
  AllegiantPayPeriod,
  getCurrentPayPeriod,
  getAdjacentPayPeriod,
  getRecentPayPeriods,
  formatDisplayDate
} from '../utils/payPeriod';

interface ProviderActivityViewProps {
  userRole: UserRole;
  onNavigateTab?: (tab: string, subTab?: string) => void;
  onSelectClient?: (id: string) => void;
  embedded?: boolean;
  onLiveSliceActiveChange?: (active: boolean) => void;
}

interface ActivitySession {
  id: string;
  client_id: string;
  provider_id: string;
  dos: string;
  status?: string | null;
  start_time: string | null;
  end_time: string | null;
  service_code: string;
  duration_minutes: number;
  actual_duration_minutes: number | null;
  planned_duration_minutes: number | null;
  notes: string | null;
  completion_notes: string | null;
  first_name: string;
  last_name: string;
  provider_name: string;
  provider_role: string | null;
  provider_title: string | null;
  supervisor_id: string | null;
  supervisor_name: string | null;
  readiness_status: string | null;
  hold_reasons: string[];
  doc_id: string | null;
  doc_title: string | null;
  doc_type: string | null;
  doc_status: string | null;
  clinician_signature: string | null;
  clinician_signed_at: string | null;
  guardian_signature: string | null;
  guardian_signed_at: string | null;
  guardian_signature_status: string | null;
  supervisor_signature: string | null;
  supervisor_signed_at: string | null;
}

export const ProviderActivityView: React.FC<ProviderActivityViewProps> = ({
  userRole,
  onNavigateTab,
  onSelectClient,
  embedded = false,
  onLiveSliceActiveChange
}) => {
  // STRICT ROLE GATE: OWNER and REFERRAL_COORDINATOR (and ADMIN/OFFICE) desktop only
  const isOwner = userRole === 'OWNER' || userRole === 'ADMIN';
  const isReferralCoordinator = userRole === 'REFERRAL_COORDINATOR';
  const isAuthorizedRole = isOwner || isReferralCoordinator || userRole === 'OFFICE';

  if (!isAuthorizedRole) {
    return (
      <div className="p-8 text-center text-slate-400 bg-slate-900/60 rounded-xl border border-slate-800">
        <AlertCircle className="w-8 h-8 text-rose-400 mx-auto mb-2" />
        <h3 className="text-white font-semibold mb-1">Access Restricted</h3>
        <p className="text-sm">Provider Activity is exclusively accessible to Owner and Referral Coordinator roles.</p>
      </div>
    );
  }

  // Two Workspace Modes: PERIOD REVIEW (default) & LIVE SLICE
  const [mode, setMode] = useState<'PERIOD_REVIEW' | 'LIVE_SLICE'>('PERIOD_REVIEW');

  // Shared Context: Therapist, Period, & Client
  const [selectedPeriod, setSelectedPeriod] = useState<AllegiantPayPeriod>(() => getCurrentPayPeriod());
  const [selectedProviderId, setSelectedProviderId] = useState<string>('ALL');
  const [selectedClientId, setSelectedClientId] = useState<string>('ALL');

  // Animation trigger key for Live Slice center-out population
  const [animSequenceKey, setAnimSequenceKey] = useState<number>(0);

  // Sync mode with parent if embedded
  useEffect(() => {
    onLiveSliceActiveChange?.(mode === 'LIVE_SLICE');
  }, [mode, onLiveSliceActiveChange]);

  // Clients dropdown list
  const [clientsList, setClientsList] = useState<Array<{ id: string; name?: string; first_name?: string; last_name?: string }>>([]);

  // Fetch Clients List (for client filter)
  useEffect(() => {
    let isMounted = true;
    const loadClients = async () => {
      try {
        const res = await fetch('/api/clients');
        const contentType = res.headers.get('content-type') || '';
        if (res.ok && contentType.includes('application/json') && isMounted) {
          const list = await res.json();
          if (Array.isArray(list)) {
            setClientsList(list);
          }
        }
      } catch (err) {
        console.error('Failed to load clients for activity view:', err);
      }
    };
    loadClients();
    return () => {
      isMounted = false;
    };
  }, []);

  // Providers dropdown list
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);

  // Sessions dataset from server-side query
  const [sessions, setSessions] = useState<ActivitySession[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search filter within active session list
  const [searchFilter, setSearchFilter] = useState('');

  // Fetch Providers List (for dropdown)
  useEffect(() => {
    let isMounted = true;
    const loadProviders = async () => {
      try {
        setLoadingProviders(true);
        const res = await fetch('/api/providers?includeInactive=1');
        const contentType = res.headers.get('content-type') || '';
        if (res.ok && contentType.includes('application/json') && isMounted) {
          const list: Provider[] = await res.json();
          // Sort active first, then alphabetical
          const sorted = Array.isArray(list)
            ? list.sort((a, b) => {
                if (a.active !== b.active) return (b.active || 0) - (a.active || 0);
                return (a.name || '').localeCompare(b.name || '');
              })
            : [];
          setProviders(sorted);
        }
      } catch (err) {
        console.error('Failed to load providers for activity view:', err);
      } finally {
        if (isMounted) setLoadingProviders(false);
      }
    };
    loadProviders();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch Sessions for Shared Context (Therapist + Period)
  const fetchSessions = useCallback(async () => {
    try {
      setRefreshing(true);
      setError(null);
      const params = new URLSearchParams();
      if (selectedPeriod.startDate) params.set('startDate', selectedPeriod.startDate);
      if (selectedPeriod.endDate) params.set('endDate', selectedPeriod.endDate);
      if (selectedProviderId && selectedProviderId !== 'ALL') {
        params.set('providerId', selectedProviderId);
      }

      const res = await fetch(`/api/provider-activity?${params.toString()}`);
      const contentType = res.headers.get('content-type') || '';

      if (!res.ok) {
        if (!contentType.includes('application/json')) {
          throw new Error(`Server returned HTTP ${res.status} non-JSON response.`);
        }
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || `Failed to load sessions (${res.status})`);
      }

      if (!contentType.includes('application/json')) {
        throw new Error('Server returned an unexpected non-JSON response.');
      }

      const data = await res.json();
      setSessions(Array.isArray(data.sessions) ? data.sessions : []);
    } catch (err: any) {
      console.error('Error fetching provider activity sessions:', err);
      setError(err.message || 'Error loading sessions');
    } finally {
      setRefreshing(false);
      setLoadingSessions(false);
    }
  }, [selectedPeriod.startDate, selectedPeriod.endDate, selectedProviderId]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Pay period navigation helpers
  const handlePrevPeriod = () => {
    setSelectedPeriod((prev) => getAdjacentPayPeriod(prev, -1));
  };

  const handleNextPeriod = () => {
    setSelectedPeriod((prev) => getAdjacentPayPeriod(prev, 1));
  };

  const handleResetCurrentPeriod = () => {
    setSelectedPeriod(getCurrentPayPeriod());
  };

  // Recent periods list for fast jumping
  const recentPeriods = useMemo(() => getRecentPayPeriods(6, 4), []);

  // Canonical Status Derivation Helpers (Zero Independent Truth)
  const isAssistantProvider = (s: ActivitySession): boolean => {
    const role = (s.provider_role || '').toUpperCase();
    const title = (s.provider_title || '').toUpperCase();
    return role === 'SLPA' || role === 'ASSISTANT' || title.includes('SLPA') || title.includes('ASSISTANT');
  };

  const resolveGuardianStatus = (s: ActivitySession): 'Signed' | 'Pending' | 'Overdue' | 'Waived' => {
    if (s.guardian_signature || s.guardian_signature_status === 'SIGNED') {
      return 'Signed';
    }
    if (s.guardian_signature_status === 'WAIVED_DOCUMENTED') {
      return 'Waived';
    }
    if (s.guardian_signature_status === 'OVERDUE') {
      return 'Overdue';
    }
    // For general non-signed documents requiring guardian
    return 'Pending';
  };

  const resolveSupervisorStatus = (s: ActivitySession): 'Signed' | 'Pending' | 'Not Required' => {
    if (!isAssistantProvider(s)) {
      return 'Not Required';
    }
    if (s.supervisor_signature) {
      return 'Signed';
    }
    return 'Pending';
  };

  const resolveDocStatus = (s: ActivitySession): 'Complete' | 'Pending' | 'Missing' => {
    if (!s.doc_id) {
      return 'Missing';
    }
    if (s.doc_status === 'FINALIZED' || (s.clinician_signature && s.doc_status !== 'DRAFT')) {
      return 'Complete';
    }
    return 'Pending';
  };

  const resolveBillingStatus = (s: ActivitySession): 'Ready' | 'Held' | 'Not Ready' => {
    const status = (s.readiness_status || '').toUpperCase();
    if (status === 'READY') return 'Ready';
    if (status === 'HELD') return 'Held';
    return 'Not Ready';
  };

  // Client options derived from current sessions and full clients list
  const clientOptions = useMemo(() => {
    const map = new Map<string, string>();
    sessions.forEach((s) => {
      if (s.client_id) {
        const last = s.last_name || '';
        const first = s.first_name || '';
        const name = `${last}${last && first ? ', ' : ''}${first}`.trim();
        map.set(s.client_id, name || s.client_id);
      }
    });
    clientsList.forEach((c) => {
      if (c.id) {
        const last = c.last_name || '';
        const first = c.first_name || '';
        const name = `${last}${last && first ? ', ' : ''}${first}`.trim() || c.name || c.id;
        if (!map.has(c.id)) {
          map.set(c.id, name);
        }
      }
    });
    return Array.from(map.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [sessions, clientsList]);

  // Filtered sessions for presentation (composes Client + Therapist + Period + optional text search in Period Review)
  const filteredSessions = useMemo(() => {
    let result = sessions;
    if (selectedClientId && selectedClientId !== 'ALL') {
      result = result.filter((s) => s.client_id === selectedClientId);
    }
    if (searchFilter.trim() && mode === 'PERIOD_REVIEW') {
      const q = searchFilter.toLowerCase();
      result = result.filter((s) => {
        const clientName = `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase();
        const provName = (s.provider_name || '').toLowerCase();
        const code = (s.service_code || '').toLowerCase();
        const docTitle = (s.doc_title || '').toLowerCase();
        return (
          clientName.includes(q) ||
          provName.includes(q) ||
          code.includes(q) ||
          docTitle.includes(q) ||
          s.dos.includes(q)
        );
      });
    }
    return result;
  }, [sessions, selectedClientId, searchFilter, mode]);

  // LIVE SLICE: Strictly deterministic chronological ordering (DOS, then start_time)
  const chronologicalSessions = useMemo(() => {
    return [...filteredSessions].sort((a, b) => {
      if (a.dos !== b.dos) return a.dos.localeCompare(b.dos);
      return (a.start_time || '00:00').localeCompare(b.start_time || '00:00');
    });
  }, [filteredSessions]);

  // Adaptive Density Tier Calculation
  // LOW: 1-6 sessions (larger, roomier tiles, fuller secondary labels)
  // MEDIUM: 7-15 sessions (medium tiles, tighter gaps, compact status text)
  // HIGH: 16+ sessions (smaller tiles, tighter spacing, condensed status chips)
  const sessionCount = chronologicalSessions.length;
  const densityTier: 'LOW' | 'MEDIUM' | 'HIGH' = useMemo(() => {
    if (sessionCount <= 6) return 'LOW';
    if (sessionCount <= 15) return 'MEDIUM';
    return 'HIGH';
  }, [sessionCount]);

  // Ultra-Minimal Live Slice Helpers
  const formatShortDos = (dos?: string): string => {
    if (!dos) return '';
    if (dos.includes('-')) {
      const parts = dos.split('-');
      if (parts.length === 3) {
        return `${parts[1]}/${parts[2]}`;
      }
    }
    if (dos.includes('/')) {
      const parts = dos.split('/');
      if (parts.length >= 2) {
        return `${parts[0].padStart(2, '0')}/${parts[1].padStart(2, '0')}`;
      }
    }
    return dos;
  };

  const getProviderInitials = (name?: string): string => {
    if (!name) return '—';
    const clean = name.trim();
    if (!clean) return '—';
    const parts = clean.split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const getCleanRole = (s: ActivitySession): string => {
    const role = (s.provider_role || '').toUpperCase();
    const title = (s.provider_title || '').toUpperCase();
    if (role === 'SLPA' || role === 'ASSISTANT' || title.includes('SLPA') || title.includes('ASSISTANT')) {
      return 'SLPA';
    }
    if (role === 'SLP' || title.includes('SLP')) {
      return 'SLP';
    }
    return role || 'SLP';
  };

  // Status indicator resolvers with 4 compact fixed-position dots
  // GREEN = good / complete / ready
  // AMBER = pending
  // PURPLE = supervisor pending
  // RED = overdue / missing / held
  // SLATE = not applicable
  const getGuardianStatusDot = (s: ActivitySession) => {
    const status = resolveGuardianStatus(s);
    if (status === 'Signed') {
      return {
        dotClass: 'bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.6)]',
        title: 'Guardian: Signed'
      };
    }
    if (status === 'Waived') {
      return {
        dotClass: 'bg-emerald-400/90 shadow-[0_0_4px_rgba(52,211,153,0.4)]',
        title: 'Guardian: Waived (Documented Exemption)'
      };
    }
    if (status === 'Overdue') {
      return {
        dotClass: 'bg-rose-500 shadow-[0_0_4px_rgba(244,63,94,0.6)] animate-pulse',
        title: 'Guardian: Overdue'
      };
    }
    return {
      dotClass: 'bg-amber-400 shadow-[0_0_4px_rgba(251,191,36,0.6)]',
      title: 'Guardian: Pending'
    };
  };

  const getSupervisorStatusDot = (s: ActivitySession) => {
    const status = resolveSupervisorStatus(s);
    if (status === 'Not Required') {
      return {
        dotClass: 'bg-slate-500/70',
        title: 'Supervisor: Not Required (Licensed SLP)'
      };
    }
    if (status === 'Signed') {
      return {
        dotClass: 'bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.6)]',
        title: 'Supervisor: Signed'
      };
    }
    return {
      dotClass: 'bg-purple-400 shadow-[0_0_4px_rgba(192,132,252,0.6)]',
      title: 'Supervisor: Pending'
    };
  };

  const getDocStatusDot = (s: ActivitySession) => {
    const status = resolveDocStatus(s);
    if (status === 'Complete') {
      return {
        dotClass: 'bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.6)]',
        title: 'Docs: Complete'
      };
    }
    if (status === 'Pending') {
      return {
        dotClass: 'bg-amber-400 shadow-[0_0_4px_rgba(251,191,36,0.6)]',
        title: 'Docs: Pending'
      };
    }
    return {
      dotClass: 'bg-rose-500 shadow-[0_0_4px_rgba(244,63,94,0.6)] animate-pulse',
      title: 'Docs: Missing'
    };
  };

  const getBillingStatusDot = (s: ActivitySession) => {
    const status = resolveBillingStatus(s);
    if (status === 'Ready') {
      return {
        dotClass: 'bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.6)]',
        title: 'Billing: Ready'
      };
    }
    if (status === 'Held') {
      return {
        dotClass: 'bg-rose-500 shadow-[0_0_4px_rgba(244,63,94,0.6)] animate-pulse',
        title: 'Billing: Held'
      };
    }
    return {
      dotClass: 'bg-amber-400 shadow-[0_0_4px_rgba(251,191,36,0.6)]',
      title: 'Billing: Not Ready'
    };
  };

  // PERIOD REVIEW SUMMARY: Derived exclusively from canonical filtered records
  const periodSummary = useMemo(() => {
    const totalSessions = filteredSessions.length;
    const uniqueClientIds = new Set<string>();

    let docCompleteCount = 0;
    let guardianIssuesCount = 0;
    let supervisorIssuesCount = 0;
    let billingReadyCount = 0;
    let billingHeldCount = 0;

    filteredSessions.forEach((s) => {
      if (s.client_id) uniqueClientIds.add(s.client_id);

      // Documentation complete
      if (resolveDocStatus(s) === 'Complete') {
        docCompleteCount++;
      }

      // Guardian issues (Overdue or missing on past encounters)
      const gStatus = resolveGuardianStatus(s);
      if (gStatus === 'Overdue') {
        guardianIssuesCount++;
      }

      // Supervisor issues (SLPA sessions with pending supervisor co-sign)
      const supStatus = resolveSupervisorStatus(s);
      if (supStatus === 'Pending') {
        supervisorIssuesCount++;
      }

      // Billing readiness
      const bStatus = resolveBillingStatus(s);
      if (bStatus === 'Ready') {
        billingReadyCount++;
      } else if (bStatus === 'Held') {
        billingHeldCount++;
      }
    });

    return {
      totalSessions,
      clientsServed: uniqueClientIds.size,
      docComplete: docCompleteCount,
      guardianIssues: guardianIssuesCount,
      supervisorIssues: supervisorIssuesCount,
      billingReady: billingReadyCount,
      billingHeld: billingHeldCount
    };
  }, [filteredSessions]);

  // Click-through handler
  const handleSessionClick = (s: ActivitySession) => {
    if (s.doc_id && onNavigateTab) {
      // Open exact Clinical Document
      onNavigateTab('clinical', s.doc_id);
    } else if (s.client_id && onSelectClient) {
      // Open client chart
      onSelectClient(s.client_id);
    }
  };

  // Active Therapist Display Label
  const selectedProviderObj = providers.find((p) => p.id === selectedProviderId);
  const therapistDisplayLabel = selectedProviderObj
    ? `${selectedProviderObj.name}${selectedProviderObj.role ? `, ${selectedProviderObj.role}` : ''}`
    : 'All Therapists';

  const selectedClientObj = clientOptions.find((c) => c.id === selectedClientId);
  const clientDisplayLabel = selectedClientObj ? selectedClientObj.label : 'All Clients';

  return (
    <div className={`space-y-4 ${embedded ? '' : 'p-4 sm:p-5 max-w-7xl mx-auto'}`}>
      {/* Top Specification Header & Mode Switcher (Visible in Period Review mode) */}
      {mode === 'PERIOD_REVIEW' && (
        <div className="relative rounded-2xl border border-slate-700/80 bg-gradient-to-b from-[#18263c] via-[#141f32] to-[#101929] shadow-[0_4px_20px_rgba(0,0,0,0.45),inset_0_1px_0_0_rgba(255,255,255,0.12)] p-4 sm:p-5 overflow-hidden">
          {/* Top hairline sheen */}
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-400/35 to-transparent pointer-events-none" />

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Title & Domain Context */}
            <div className="space-y-1">
              <div className="flex items-center space-x-2.5">
                <div className="p-1.5 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-400">
                  <Activity className="w-5 h-5" />
                </div>
                <h1 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
                  <span>Provider Activity</span>
                  <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded bg-cyan-950/60 border border-cyan-700/50 text-cyan-300 font-semibold">
                    Owner / RC
                  </span>
                </h1>
              </div>
              <p className="text-xs text-slate-400">
                Therapist-centric operational review and chronological session stream for active pay period.
              </p>
            </div>

            {/* TWO MODES: Compact Pulse Segmented Control */}
            <div className="flex items-center gap-3">
              <div className="inline-flex items-center p-1 rounded-xl bg-slate-100 border border-slate-200/90">
                <button
                  type="button"
                  onClick={() => setMode('PERIOD_REVIEW')}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center space-x-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
                    mode === 'PERIOD_REVIEW'
                      ? 'bg-slate-800 text-white shadow-xs border border-slate-700'
                      : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                  }`}
                >
                  <Layers className="w-3.5 h-3.5" />
                  <span>PERIOD REVIEW</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setMode('LIVE_SLICE');
                    setAnimSequenceKey((k) => k + 1);
                  }}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center space-x-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
                    mode === 'LIVE_SLICE'
                      ? 'bg-slate-800 text-white shadow-xs border border-slate-700'
                      : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                  }`}
                >
                  <Activity className="w-3.5 h-3.5" />
                  <span>LIVE SLICE</span>
                </button>
              </div>

              <button
                type="button"
                onClick={fetchSessions}
                disabled={refreshing}
                className="p-2 rounded-xl bg-slate-800/80 hover:bg-slate-700/80 text-slate-300 hover:text-white border border-slate-700/80 transition-all cursor-pointer active:scale-95"
                title="Refresh Activity"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin text-cyan-400' : 'text-slate-400'}`} />
              </button>
            </div>
          </div>

          {/* SHARED CONTEXT CONTROLS BAR: Therapist + Client + Period */}
          <div className="mt-4 pt-4 border-t border-slate-700/60 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              {/* 1. THERAPIST SELECTOR */}
              <div className="flex items-center space-x-2">
                <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400 font-bold">
                  Therapist:
                </span>
                <div className="relative">
                  <select
                    value={selectedProviderId}
                    onChange={(e) => setSelectedProviderId(e.target.value)}
                    className="bg-white border border-slate-200/90 hover:border-slate-300 rounded-lg px-3 py-1.5 text-xs text-slate-800 font-medium focus:outline-none focus:ring-1 focus:ring-slate-400 pr-8 transition-colors cursor-pointer appearance-none max-w-[190px] truncate"
                  >
                    <option value="ALL">All Therapists (Office-Wide)</option>
                    {providers.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} {p.role ? `(${p.role})` : ''} {p.active === 0 ? '[Inactive]' : ''}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-500">
                    <User className="w-3.5 h-3.5" />
                  </div>
                </div>
              </div>

              {/* 2. CLIENT SELECTOR */}
              <div className="flex items-center space-x-2">
                <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400 font-bold">
                  Client:
                </span>
                <div className="relative">
                  <select
                    value={selectedClientId}
                    onChange={(e) => setSelectedClientId(e.target.value)}
                    className="bg-white border border-slate-200/90 hover:border-slate-300 rounded-lg px-3 py-1.5 text-xs text-slate-800 font-medium focus:outline-none focus:ring-1 focus:ring-slate-400 pr-8 transition-colors cursor-pointer appearance-none max-w-[190px] truncate"
                  >
                    <option value="ALL">All Clients ({clientOptions.length})</option>
                    {clientOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-500">
                    <Users className="w-3.5 h-3.5" />
                  </div>
                </div>
              </div>

              {/* 3. PAY PERIOD NAVIGATION */}
              <div className="flex items-center space-x-1.5">
                <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400 font-bold">
                  Period:
                </span>

                <div className="inline-flex items-center rounded-lg bg-white border border-slate-200/90 p-0.5">
                  <button
                    type="button"
                    onClick={handlePrevPeriod}
                    className="p-1 rounded text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors cursor-pointer"
                    title="Previous Pay Period"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="px-2 py-0.5 text-xs font-mono font-bold text-slate-800 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-slate-500" />
                    <span>
                      {formatDisplayDate(selectedPeriod.startDate)} – {formatDisplayDate(selectedPeriod.endDate)}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={handleNextPeriod}
                    className="p-1 rounded text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors cursor-pointer"
                    title="Next Pay Period"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {!selectedPeriod.isCurrent && (
                  <button
                    type="button"
                    onClick={handleResetCurrentPeriod}
                    className="px-2 py-1 rounded-md bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-800 text-[10.5px] font-mono font-semibold transition-colors cursor-pointer"
                    title="Jump to Current Pay Period"
                  >
                    Current Period
                  </button>
                )}
              </div>
            </div>

            {/* Quick Context Summary Tag */}
            <div className="flex items-center gap-2 text-xs font-mono text-slate-400">
              <span>Filtering:</span>
              <span className="text-white font-semibold">{therapistDisplayLabel}</span>
              {selectedClientId !== 'ALL' && (
                <>
                  <span>•</span>
                  <span className="text-sky-300 font-semibold">{clientDisplayLabel}</span>
                </>
              )}
              <span>•</span>
              <span className="text-emerald-400 font-semibold">{filteredSessions.length} Sessions</span>
            </div>
          </div>
        </div>
      )}

      {/* Error Banner if any */}
      {error && (
        <div className="p-3 rounded-xl bg-rose-950/50 border border-rose-700/60 text-rose-200 text-xs flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
            <span>{error}</span>
          </div>
          <button
            type="button"
            onClick={fetchSessions}
            className="text-xs font-bold underline hover:text-white cursor-pointer"
          >
            Retry
          </button>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODE 1: PERIOD REVIEW                                                     */}
      {/* ========================================================================= */}
      {mode === 'PERIOD_REVIEW' && (
        <div className="space-y-4">
          {/* Top Summary: 7 Compact Canonical Operational Tiles (Zero Financials) */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
            {/* 1. SESSIONS IN PERIOD */}
            <div className="p-3 rounded-xl bg-white border border-slate-200 shadow-xs space-y-1">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-700 font-semibold flex items-center justify-between">
                <span>Sessions</span>
                <Activity className="w-3.5 h-3.5 text-sky-700" />
              </div>
              <div className="text-xl font-bold font-mono text-slate-900">
                {periodSummary.totalSessions}
              </div>
              <div className="text-[9.5px] text-slate-600">In selected period</div>
            </div>

            {/* 2. CLIENTS SERVED */}
            <div className="p-3 rounded-xl bg-white border border-slate-200 shadow-xs space-y-1">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-700 font-semibold flex items-center justify-between">
                <span>Clients</span>
                <Users className="w-3.5 h-3.5 text-sky-700" />
              </div>
              <div className="text-xl font-bold font-mono text-slate-900">
                {periodSummary.clientsServed}
              </div>
              <div className="text-[9.5px] text-slate-600">Unique client charts</div>
            </div>

            {/* 3. DOCUMENTATION COMPLETE */}
            <div className="p-3 rounded-xl bg-white border border-emerald-200 shadow-xs space-y-1">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-700 font-semibold flex items-center justify-between">
                <span>Doc Complete</span>
                <FileText className="w-3.5 h-3.5 text-emerald-700" />
              </div>
              <div className="text-xl font-bold font-mono text-emerald-800">
                {periodSummary.docComplete}
              </div>
              <div className="text-[9.5px] text-slate-600">Signed & Finalized</div>
            </div>

            {/* 4. GUARDIAN ISSUES */}
            <div className="p-3 rounded-xl bg-white border border-amber-200 shadow-xs space-y-1">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-700 font-semibold flex items-center justify-between">
                <span>Guardian Issues</span>
                <Clock className="w-3.5 h-3.5 text-amber-700" />
              </div>
              <div className="text-xl font-bold font-mono text-amber-800">
                {periodSummary.guardianIssues}
              </div>
              <div className="text-[9.5px] text-slate-600">Overdue / Missing</div>
            </div>

            {/* 5. SUPERVISOR ISSUES */}
            <div className="p-3 rounded-xl bg-white border border-purple-200 shadow-xs space-y-1">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-700 font-semibold flex items-center justify-between">
                <span>Supervisor Issues</span>
                <ShieldCheck className="w-3.5 h-3.5 text-purple-700" />
              </div>
              <div className="text-xl font-bold font-mono text-purple-800">
                {periodSummary.supervisorIssues}
              </div>
              <div className="text-[9.5px] text-slate-600">Pending SLPA co-sign</div>
            </div>

            {/* 6. BILLING READY */}
            <div className="p-3 rounded-xl bg-white border border-emerald-200 shadow-xs space-y-1">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-700 font-semibold flex items-center justify-between">
                <span>Billing Ready</span>
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
              </div>
              <div className="text-xl font-bold font-mono text-emerald-800">
                {periodSummary.billingReady}
              </div>
              <div className="text-[9.5px] text-slate-600">Cleared for claims</div>
            </div>

            {/* 7. BILLING HELD */}
            <div className="p-3 rounded-xl bg-white border border-rose-200 shadow-xs space-y-1">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-700 font-semibold flex items-center justify-between">
                <span>Billing Held</span>
                <AlertTriangle className="w-3.5 h-3.5 text-rose-700" />
              </div>
              <div className="text-xl font-bold font-mono text-rose-800">
                {periodSummary.billingHeld}
              </div>
              <div className="text-[9.5px] text-slate-600">Held prerequisites</div>
            </div>
          </div>

          {/* Session Table & Search Bar */}
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-xs">
            {/* Table Header Bar */}
            <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 bg-slate-50">
              <div className="flex items-center space-x-2">
                <span className="text-xs font-bold text-slate-800 uppercase tracking-wider font-mono">
                  Rendered Sessions ({filteredSessions.length})
                </span>
                <span className="text-[11px] text-slate-500">
                  • Click any row to inspect linked document
                </span>
              </div>

              {/* In-table search input */}
              <div className="relative w-64">
                <input
                  type="text"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  placeholder="Filter client, CPT, notes..."
                  className="w-full bg-white border border-slate-300 rounded-lg pl-8 pr-3 py-1.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-400"
                />
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
              </div>
            </div>

            {/* Table Body */}
            {loadingSessions ? (
              <div className="p-12 text-center text-slate-500 space-y-2 bg-white">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
                <p className="text-xs">Loading provider sessions...</p>
              </div>
            ) : filteredSessions.length === 0 ? (
              <div className="p-12 text-center text-slate-500 space-y-2 bg-white">
                <Calendar className="w-8 h-8 text-slate-400 mx-auto" />
                <p className="text-sm font-semibold text-slate-800">No sessions recorded in this period</p>
                <p className="text-xs text-slate-500">
                  {selectedProviderId !== 'ALL'
                    ? `No encounters found for ${therapistDisplayLabel} between ${formatDisplayDate(selectedPeriod.startDate)} and ${formatDisplayDate(selectedPeriod.endDate)}.`
                    : 'No encounters found office-wide for the selected date range.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-slate-700 bg-[#162030] text-slate-200 font-mono text-[10.5px] uppercase tracking-wider">
                      <th className="py-2.5 px-3">DOS / Time</th>
                      <th className="py-2.5 px-3">Client</th>
                      <th className="py-2.5 px-3">Therapist</th>
                      <th className="py-2.5 px-3">CPT / Duration</th>
                      <th className="py-2.5 px-3">Documentation</th>
                      <th className="py-2.5 px-3">Signatures</th>
                      <th className="py-2.5 px-3">Billing</th>
                      <th className="py-2.5 px-3 text-right">Inspect</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white font-sans">
                    {filteredSessions.map((s) => {
                      const noShow = String(s.status || '').toUpperCase() === 'NO_SHOW';
                      const docStatus = resolveDocStatus(s);
                      const guardianStatus = resolveGuardianStatus(s);
                      const supervisorStatus = resolveSupervisorStatus(s);
                      const billingStatus = resolveBillingStatus(s);

                      return (
                        <tr
                          key={s.id}
                          onClick={() => handleSessionClick(s)}
                          className="even:bg-slate-50/60 hover:bg-slate-100/80 transition-colors cursor-pointer group"
                        >
                          {/* 1. DOS / Time */}
                          <td className="py-3 px-3 whitespace-nowrap">
                            <div className="font-mono font-bold text-slate-900 text-[12.5px]">{s.dos}</div>
                            {s.start_time && (
                              <div className="text-[11px] font-mono text-cyan-700 font-semibold">{s.start_time}</div>
                            )}
                          </td>

                          {/* 2. Client */}
                          <td className="py-3 px-3 whitespace-nowrap">
                            <div className="font-semibold text-slate-900 group-hover:text-cyan-700 transition-colors">
                              {s.first_name} {s.last_name}
                            </div>
                            <div className="text-[10.5px] font-mono text-slate-500">ID: {s.client_id.slice(0, 8)}</div>
                          </td>

                          {/* 3. Therapist */}
                          <td className="py-3 px-3 whitespace-nowrap">
                            <div className="font-medium text-slate-800">{s.provider_name}</div>
                            {s.provider_role && (
                              <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-slate-100 border border-slate-200 text-slate-700 font-medium">
                                {s.provider_role}
                              </span>
                            )}
                          </td>

                          {/* 4. CPT / Duration */}
                          <td className="py-3 px-3 whitespace-nowrap">
                            {noShow ? (
                              <div className="font-semibold text-slate-800">Client No-Show</div>
                            ) : (
                            <div className="font-mono font-bold text-sky-700">{s.service_code}</div>
                            )}
                            {!noShow && (
                            <div className="text-[11px] font-mono text-slate-600">
                              <span>{s.actual_duration_minutes || s.duration_minutes || 60}m actual</span>
                              {s.planned_duration_minutes && s.planned_duration_minutes !== (s.actual_duration_minutes || s.duration_minutes) && (
                                <span className="text-slate-400 text-[10px] ml-1">({s.planned_duration_minutes}m pl)</span>
                              )}
                            </div>
                            )}
                          </td>

                          {/* 5. Documentation */}
                          <td className="py-3 px-3">
                            {noShow ? <span className="text-slate-400">—</span> : (<>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span
                                className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold ${
                                  docStatus === 'Complete'
                                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-300'
                                    : docStatus === 'Pending'
                                    ? 'bg-amber-50 text-amber-800 border border-amber-300'
                                    : 'bg-rose-50 text-rose-800 border border-rose-300'
                                }`}
                              >
                                {docStatus.toUpperCase()}
                              </span>
                              {s.doc_type && (
                                <span className="text-[10px] font-mono text-slate-500 uppercase font-medium">
                                  {s.doc_type}
                                </span>
                              )}
                            </div>
                            {s.doc_title && (
                              <div className="text-[11px] text-slate-500 truncate max-w-[180px] mt-0.5">
                                {s.doc_title}
                              </div>
                            )}
                            </>)}
                          </td>

                          {/* 6. Signatures (Clinician, Guardian, Supervisor) */}
                          <td className="py-3 px-3 whitespace-nowrap">
                            {noShow ? <span className="text-slate-400">—</span> : (<>
                            <div className="space-y-0.5 font-mono text-[10.5px]">
                              {/* Clinician */}
                              <div className="flex items-center gap-1">
                                <span className="text-slate-400 w-11">Clin:</span>
                                {s.clinician_signature ? (
                                  <span className="text-emerald-700 font-semibold">✓ Signed</span>
                                ) : (
                                  <span className="text-amber-700 font-semibold">Pending</span>
                                )}
                              </div>

                              {/* Guardian */}
                              <div className="flex items-center gap-1">
                                <span className="text-slate-400 w-11">Guard:</span>
                                {guardianStatus === 'Signed' ? (
                                  <span className="text-emerald-700 font-semibold">✓ Signed</span>
                                ) : guardianStatus === 'Waived' ? (
                                  <span className="text-sky-700 font-semibold">Waived</span>
                                ) : guardianStatus === 'Overdue' ? (
                                  <span className="text-rose-700 font-bold">Overdue</span>
                                ) : (
                                  <span className="text-amber-700 font-semibold">Pending</span>
                                )}
                              </div>

                              {/* Supervisor */}
                              <div className="flex items-center gap-1">
                                <span className="text-slate-400 w-11">Sup:</span>
                                {supervisorStatus === 'Not Required' ? (
                                  <span className="text-slate-400">Not Req</span>
                                ) : supervisorStatus === 'Signed' ? (
                                  <span className="text-emerald-700 font-semibold">✓ Signed</span>
                                ) : (
                                  <span className="text-indigo-700 font-semibold">Pending</span>
                                )}
                              </div>
                            </div>
                            </>)}
                          </td>

                          {/* 7. Billing Readiness */}
                          <td className="py-3 px-3 whitespace-nowrap">
                            {noShow ? <span className="text-slate-400">—</span> : (
                            <>
                            <span
                              className={`px-2.5 py-0.5 rounded text-[10.5px] font-mono font-bold ${
                                billingStatus === 'Ready'
                                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-300'
                                  : billingStatus === 'Held'
                                  ? 'bg-rose-50 text-rose-800 border border-rose-300'
                                  : 'bg-amber-50 text-amber-800 border border-amber-300'
                              }`}
                            >
                              {billingStatus.toUpperCase()}
                            </span>
                            {s.hold_reasons && s.hold_reasons.length > 0 && (
                              <div className="text-[10px] text-rose-700 truncate max-w-[130px] mt-0.5">
                                {s.hold_reasons[0]}
                              </div>
                            )}
                            </>)}
                          </td>

                          {/* 8. Inspect Action */}
                          <td className="py-3 px-3 text-right">
                            <button
                              type="button"
                              className="p-1 rounded text-slate-400 group-hover:text-cyan-700 group-hover:bg-slate-100 transition-colors"
                              title={s.doc_id ? 'Inspect Clinical Document' : 'Open Client Chart'}
                            >
                              <ArrowRight className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODE 2: LIVE SLICE (Focused Full-Screen Adaptive Session Field)           */}
      {/* ========================================================================= */}
      {mode === 'LIVE_SLICE' && (
        <div className="w-full space-y-4">
          {/* 1. COMPACT FOCUS HEADER (Single-Row Command Bar) */}
          <div className="relative rounded-2xl border border-slate-700/80 bg-gradient-to-b from-[#18263c] via-[#141f32] to-[#101929] shadow-[0_4px_20px_rgba(0,0,0,0.45),inset_0_1px_0_0_rgba(255,255,255,0.12)] px-3.5 py-2.5 sm:px-4 sm:py-2.5 overflow-hidden">
            {/* Top specular hairline */}
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent pointer-events-none" />

            <div className="flex flex-wrap items-center justify-between gap-2 w-full min-w-0">
              {/* Left: Back Control + Mode Identity + Density (with session count) */}
              <div className="flex items-center space-x-2.5 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setMode('PERIOD_REVIEW');
                    onLiveSliceActiveChange?.(false);
                  }}
                  className="inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-lg bg-slate-900/90 hover:bg-slate-800 border border-slate-700/90 hover:border-cyan-500/60 text-xs font-semibold text-slate-300 hover:text-white transition-all cursor-pointer group shadow-sm active:scale-95 shrink-0"
                  title="Return to Provider Activity workspace"
                >
                  <ArrowLeft className="w-3.5 h-3.5 text-cyan-400 group-hover:-translate-x-0.5 transition-transform" />
                  <span>Back to Provider Activity</span>
                </button>

                <div className="flex items-center space-x-2 pl-2.5 border-l border-slate-700/80 shrink-0">
                  <span className="relative flex h-2 w-2 shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500" />
                  </span>
                  <span className="text-xs font-mono font-bold tracking-wider uppercase text-white whitespace-nowrap">
                    LIVE SLICE
                  </span>
                  <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded font-bold whitespace-nowrap shrink-0 ${
                    densityTier === 'LOW'
                      ? 'bg-blue-950/70 text-blue-300 border border-blue-800/60'
                      : densityTier === 'MEDIUM'
                      ? 'bg-cyan-950/70 text-cyan-300 border border-cyan-800/60'
                      : 'bg-teal-950/70 text-teal-300 border border-teal-800/60'
                  }`}>
                    {densityTier} DENSITY ({chronologicalSessions.length})
                  </span>
                </div>
              </div>

              {/* Right: Therapist, Client, Period, Refresh */}
              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                {/* 1. THERAPIST FILTER (Direct select without redundant label) */}
                <div className="relative shrink-0">
                  <select
                    value={selectedProviderId}
                    onChange={(e) => {
                      setSelectedProviderId(e.target.value);
                      setAnimSequenceKey((k) => k + 1);
                    }}
                    title="Filter by Therapist"
                    className="bg-[#3d5270] border border-slate-400/45 hover:border-slate-300/70 rounded-lg px-2.5 py-1 text-xs text-white font-medium focus:outline-none focus:ring-1 focus:ring-slate-300 pr-7 transition-colors cursor-pointer appearance-none max-w-[165px] sm:max-w-[185px] truncate"
                  >
                    <option value="ALL">All Therapists</option>
                    {providers.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} {p.role ? `(${p.role})` : ''}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-1.5 text-slate-200">
                    <User className="w-3 h-3" />
                  </div>
                </div>

                {/* 2. CLIENT FILTER (Direct select without redundant label) */}
                <div className="relative shrink-0">
                  <select
                    value={selectedClientId}
                    onChange={(e) => {
                      setSelectedClientId(e.target.value);
                      setAnimSequenceKey((k) => k + 1);
                    }}
                    title="Filter by Client"
                    className="bg-[#3d5270] border border-slate-400/45 hover:border-slate-300/70 rounded-lg px-2.5 py-1 text-xs text-white font-medium focus:outline-none focus:ring-1 focus:ring-slate-300 pr-7 transition-colors cursor-pointer appearance-none max-w-[155px] sm:max-w-[175px] truncate"
                  >
                    <option value="ALL">All Clients ({clientOptions.length})</option>
                    {clientOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-1.5 text-slate-200">
                    <Users className="w-3 h-3" />
                  </div>
                </div>

                {/* 3. PERIOD NAVIGATOR (Direct controls without redundant label) */}
                <div className="inline-flex items-center rounded-lg bg-[#3d5270] border border-slate-400/45 p-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      handlePrevPeriod();
                      setAnimSequenceKey((k) => k + 1);
                    }}
                    className="p-1 rounded text-slate-200 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                    title="Previous Pay Period"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>

                  <div className="px-2 py-0.5 text-xs font-mono font-bold text-white flex items-center gap-1 whitespace-nowrap">
                    <Calendar className="w-3 h-3 text-slate-200" />
                    <span>
                      {formatDisplayDate(selectedPeriod.startDate)} – {formatDisplayDate(selectedPeriod.endDate)}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      handleNextPeriod();
                      setAnimSequenceKey((k) => k + 1);
                    }}
                    className="p-1 rounded text-slate-200 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                    title="Next Pay Period"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* 4. REFRESH ACTION */}
                <button
                  type="button"
                  onClick={() => {
                    fetchSessions();
                    setAnimSequenceKey((k) => k + 1);
                  }}
                  disabled={refreshing}
                  className="p-1.5 rounded-lg bg-slate-900/80 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-700 transition-all cursor-pointer active:scale-95 shrink-0"
                  title="Refresh Sessions"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin text-cyan-400' : 'text-slate-400'}`} />
                </button>
              </div>
            </div>
          </div>

          {/* 2. ADAPTIVE DENSITY SESSION FIELD */}
          <div className="w-full">
            {loadingSessions ? (
              <div className="p-16 text-center text-slate-400 space-y-2.5">
                <RefreshCw className="w-7 h-7 animate-spin text-cyan-400 mx-auto" />
                <p className="text-sm font-medium text-slate-300">Populating live slice encounters...</p>
                <p className="text-xs text-slate-500 font-mono">
                  Loading sessions for {formatDisplayDate(selectedPeriod.startDate)} – {formatDisplayDate(selectedPeriod.endDate)}
                </p>
              </div>
            ) : chronologicalSessions.length === 0 ? (
              <div className="p-16 text-center text-slate-500 bg-white rounded-2xl border border-slate-200 shadow-xs space-y-3 max-w-xl mx-auto">
                <Calendar className="w-10 h-10 text-slate-400 mx-auto" />
                <h3 className="text-base font-semibold text-slate-800">No encounters found in this period</h3>
                <p className="text-xs text-slate-500">
                  There are no recorded sessions matching the selected Therapist and Client filters for this pay period.
                  Switch periods or select "All Therapists" / "All Clients" above.
                </p>
              </div>
            ) : (
              <div
                className={`w-full ${
                  densityTier === 'LOW'
                    ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-w-5xl mx-auto'
                    : densityTier === 'MEDIUM'
                    ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5 max-w-7xl mx-auto'
                    : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-2 w-full mx-auto'
                }`}
              >
                {chronologicalSessions.map((s, idx) => {
                  // Center-out animation timing calculation
                  // Center index of current list
                  const centerIdx = (sessionCount - 1) / 2;
                  const maxDist = Math.max(centerIdx, 1);
                  const distFromCenter = Math.abs(idx - centerIdx);
                  const normalizedDist = distFromCenter / maxDist;
                  const charCode = s.id ? s.id.charCodeAt(s.id.length - 1) : idx;
                  const jitter = (charCode % 4) * 0.01;
                  const animDelay = Number((normalizedDist * 0.22 + jitter).toFixed(3));

                  // Compact Status Dots
                  const gStatus = getGuardianStatusDot(s);
                  const supStatus = getSupervisorStatusDot(s);
                  const docStatus = getDocStatusDot(s);
                  const billStatus = getBillingStatusDot(s);

                  const noShow = String(s.status || '').toUpperCase() === 'NO_SHOW';
                  const duration = noShow ? null : (s.actual_duration_minutes || s.duration_minutes || 60);
                  const providerInitials = getProviderInitials(s.provider_name);
                  const cleanRole = getCleanRole(s);

                  return (
                    <motion.div
                      key={`slice-${s.id}-${animSequenceKey}`}
                      initial={{ opacity: 0, scale: 0.92, y: 6 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      transition={{
                        duration: 0.24,
                        delay: animDelay,
                        ease: [0.16, 1, 0.3, 1]
                      }}
                      onClick={() => handleSessionClick(s)}
                      className={`relative rounded-xl border border-slate-200/90 bg-white hover:bg-slate-50/90 hover:border-slate-300 shadow-xs hover:shadow-sm transition-all cursor-pointer group flex flex-col justify-between overflow-hidden select-none active:scale-[0.99] ${
                        densityTier === 'LOW'
                          ? 'p-3.5 min-h-[92px]'
                          : densityTier === 'MEDIUM'
                          ? 'p-3 min-h-[84px]'
                          : 'p-2.5 min-h-[76px]'
                      }`}
                    >
                      {/* UPPER SECTION: Client Name & Visit Time (Dominant) + Short DOS & Duration */}
                      <div>
                        {/* Priority 1: CLIENT NAME & VISIT TIME (Visually Dominant) */}
                        <div className="flex items-baseline justify-between gap-1.5 min-w-0">
                          <div
                            className={`font-bold text-slate-900 tracking-tight uppercase truncate group-hover:text-cyan-700 transition-colors ${
                              densityTier === 'LOW'
                                ? 'text-[13px] sm:text-sm'
                                : densityTier === 'MEDIUM'
                                ? 'text-xs sm:text-[12.5px]'
                                : 'text-[11.5px]'
                            }`}
                            title={`${s.first_name} ${s.last_name}`}
                          >
                            {s.first_name} {s.last_name}
                          </div>

                          <div
                            className={`font-mono font-bold text-cyan-800 shrink-0 text-right ${
                              densityTier === 'LOW'
                                ? 'text-xs sm:text-[12.5px]'
                                : densityTier === 'MEDIUM'
                                ? 'text-[11px] sm:text-xs'
                                : 'text-[10.5px]'
                            }`}
                          >
                            {noShow ? 'Client No-Show' : (s.start_time || 'TIME N/A')}
                          </div>
                        </div>

                        {/* Priority 2: DOS · DURATION (Very small secondary text) */}
                        <div className="font-mono text-[10px] text-slate-500 font-medium mt-0.5 flex items-center gap-1">
                          <span>{formatShortDos(s.dos)}</span>
                          {!noShow && (
                            <>
                              <span className="text-slate-300">·</span>
                              <span>{duration}m</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* LOWER SECTION: Therapist Identity + 4 Fixed-Position Status Dots */}
                      <div className="flex items-center justify-between gap-2 mt-2 pt-1.5 border-t border-slate-100">
                        {/* Therapist Identity: Initials badge + small role chip (quieter if filtered by therapist) */}
                        <div
                          className={`flex items-center gap-1.5 shrink-0 ${
                            selectedProviderId !== 'ALL' ? 'opacity-65' : ''
                          }`}
                          title={`Therapist: ${s.provider_name} (${cleanRole})`}
                        >
                          <div className="w-5 h-5 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center text-[9px] font-mono font-bold text-slate-700 shrink-0">
                            {providerInitials}
                          </div>
                          <span className="text-[9px] font-mono font-semibold px-1 py-0.2 rounded bg-slate-100 border border-slate-200 text-slate-600 shrink-0">
                            {cleanRole}
                          </span>
                        </div>

                        {/* Status: 4 Fixed-Position Compact Indicators (G, S, D, B) */}
                        {!noShow && (
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span title={gStatus.title} className="inline-flex items-center gap-0.5 font-mono text-[8.5px] font-bold text-slate-500">
                            <span className="text-slate-400">G</span>
                            <span className={`w-1.5 h-1.5 rounded-full ${gStatus.dotClass}`} />
                          </span>
                          <span title={supStatus.title} className="inline-flex items-center gap-0.5 font-mono text-[8.5px] font-bold text-slate-500">
                            <span className="text-slate-400">S</span>
                            <span className={`w-1.5 h-1.5 rounded-full ${supStatus.dotClass}`} />
                          </span>
                          <span title={docStatus.title} className="inline-flex items-center gap-0.5 font-mono text-[8.5px] font-bold text-slate-500">
                            <span className="text-slate-400">D</span>
                            <span className={`w-1.5 h-1.5 rounded-full ${docStatus.dotClass}`} />
                          </span>
                          <span title={billStatus.title} className="inline-flex items-center gap-0.5 font-mono text-[8.5px] font-bold text-slate-500">
                            <span className="text-slate-400">B</span>
                            <span className={`w-1.5 h-1.5 rounded-full ${billStatus.dotClass}`} />
                          </span>
                        </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
